import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { AssetWriteOffService } from '../services/asset-write-off/asset-write-off.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { CenterService } from '../services/center/center.service';
import { MastersService } from '../services/masters/masters.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom, Subscription } from 'rxjs';
import { ListStateService } from '../services/list-state/list-state.service';
import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent,
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { openInNewTab } from '../utils/url.utils';

@Component({
  selector: 'lib-asset-write-off-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './asset-write-off-list.html',
  styleUrls: ['./asset-write-off-list.css'],
})
export class AssetWriteOffList implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  tableData: any[] = [];
  totalCount = 0;
  isLoading = false;
  pageIndex = 1;
  rowsPerPage = 10;
  totalPages = 0;
  columnPreferenceId?: string;
  exportFilename = `asset_write_offs_${new Date().toISOString().slice(0, 10)}`;

  // Filters
  assetIdSearch: any = null; // Changed to object for autocomplete
  assetNameQuery: any = null; // Changed to object for autocomplete
  locationId: number | null = null;
  centerId: number | null = null;
  
  subsidiariesRawData: any[] = [];
  locationOptions: any[] = [];
  centerOptions: any[] = [];

  filterConfigs: FilterConfig[] = [];
  // Column configurations - dynamically updated from backend column_preference
  columnConfigs: ColumnConfig[] = [];

  actionConfigs: ActionConfig[] = [
    { key: 'view', label: 'View', tooltip: 'View', severity: 'secondary', svgIcon: SVG_ICONS.VIEW },
    { key: 'edit', label: 'Edit', tooltip: 'Edit', severity: 'secondary', svgIcon: SVG_ICONS.EDIT },
    { key: 'delete', label: 'Delete', tooltip: 'Delete', severity: 'secondary', svgIcon: SVG_ICONS.DELETE,
      disabled: (row) => row.raw?.status === 'completed',
      disabledTooltip: 'Cannot delete completed requests'
    },
  ];

  constructor(
    private writeOffService: AssetWriteOffService,
    private subsidiaryService: SubsidiaryService,
    private centerService: CenterService,
    private mastersService: MastersService,
    private router: Router,
    private toasterService: ToasterService,
    private dialogService: DialogService,
    private exportApiService: ExportApiService,
    // Using simple approach for autocomplete: hit write-off list since we just want to filter write offs anyway
  ) {}

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  async ngOnInit() {
    try {
      await this.loadFiltersData();
      this.initializeFilterConfigs();
      
      this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
        this.loadWriteOffs(page);
      });
    } catch (error) {
      console.error('Error in ngOnInit:', error);
      this.isLoading = false;
    }
  }

  async loadFiltersData() {
    try {
      const subRes = await lastValueFrom(this.subsidiaryService.getSubsidiaries({ limit: '1000', is_active: 'true' }));
      if (subRes?.data?.subsidiaries) {
        this.subsidiariesRawData = subRes.data.subsidiaries;
        this.centerOptions = this.subsidiariesRawData.map((s: any) => ({
          label: s.name,
          value: s.id,
        }));
      }
      
      this.updateLocationOptions(this.centerId);
    } catch (error) {
      console.error('Error loading filter data:', error);
    }
  }

  updateLocationOptions(subsidiaryId: any) {
    if (!subsidiaryId) {
      this.locationOptions = [];
    } else {
      const subsidiary = this.subsidiariesRawData.find((s: any) => String(s.id) === String(subsidiaryId));
      if (subsidiary?.tax_information && Array.isArray(subsidiary.tax_information)) {
        this.locationOptions = subsidiary.tax_information
          .filter((t: any) => !t.is_deleted)
          .map((t: any) => ({
            label: t.address || `Location ${t.id}`,
            value: t.id
          }));
      } else {
        this.locationOptions = [];
      }
    }
    const locationFilterIndex = this.filterConfigs.findIndex(f => f.key === 'locationId');
    if (locationFilterIndex !== -1) {
      this.filterConfigs[locationFilterIndex].options = this.locationOptions;
      this.filterConfigs = [...this.filterConfigs];
    }
  }

  async handleAssetSearch(query: string, filter: FilterConfig, searchType: 'id' | 'name') {
    try {
      const res = await lastValueFrom(this.writeOffService.getAssetWriteOffs({ limit: 20, search: query }));
      if (res?.data?.data) {
        const results = res.data.data;
        const suggestions = results.map((item: any) => ({
          label: searchType === 'id' ? (item.asset_code || item.write_off_id) : (item.asset_name || item.asset_code),
          value: searchType === 'id' ? item.asset_code : item.asset_name
        }));
        
        // Remove duplicates
        const uniqueSuggestions = Array.from(new Map(suggestions.map((item: any) => [item.label, item])).values());
        
        const filterIndex = this.filterConfigs.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filterConfigs[filterIndex].suggestions = uniqueSuggestions;
        }
      }
    } catch (error) {
      console.error('Error searching assets:', error);
    }
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'assetIdSearch',
        type: 'autocomplete',
        placeholder: 'Asset ID',
        value: this.assetIdSearch,
        showInAccordion: true,
        showInDialog: false,
        forceSelection: false,
        completeMethod: async (event: { query: string; filter: FilterConfig }) => {
          await this.handleAssetSearch(event.query, event.filter, 'id');
        }
      },
      {
        key: 'assetNameQuery',
        type: 'autocomplete',
        placeholder: 'Asset Name',
        value: this.assetNameQuery,
        showInAccordion: true,
        showInDialog: false,
        forceSelection: false,
        completeMethod: async (event: { query: string; filter: FilterConfig }) => {
          await this.handleAssetSearch(event.query, event.filter, 'name');
        }
      },
      {
        key: 'centerId',
        type: 'select',
        placeholder: 'Center',
        value: this.centerId,
        options: this.centerOptions,
        showInAccordion: true,
        showInDialog: false,
      },
      {
        key: 'locationId',
        type: 'select',
        placeholder: 'Location',
        value: this.locationId,
        options: this.locationOptions,
        showInAccordion: true,
        showInDialog: false,
      },
    ];
  }

  async loadWriteOffs(page = 1, skipLoading = false) {
    try {
      if (!skipLoading) this.isLoading = true;
      this.pageIndex = page;

      const filters: any = {
        limit: this.rowsPerPage,
        offset: (page - 1) * this.rowsPerPage,
        get_references: 'true',
      };

      if (this.assetIdSearch) filters.asset_id = this.assetIdSearch; // Using API support
      if (this.assetNameQuery) filters.search = this.assetNameQuery; // Using API support
      
      if (this.locationId) filters.location_id = this.locationId;
      if (this.centerId) filters.subsidiary_id = this.centerId;

      const res = await lastValueFrom(this.writeOffService.getAssetWriteOffs(filters));

      if (res?.data && res.status === 'success') {
        const dataArr = res.data.data || res.data;
        
        // Handle column preference
        if (res.data.column_preference) {
          const colPref = res.data.column_preference;
          this.columnPreferenceId = colPref.id ? String(colPref.id) : undefined;
          
          if (colPref.columns_list && Array.isArray(colPref.columns_list)) {
            this.columnConfigs = colPref.columns_list.map((col: any) => ({
              key: col.key,
              header: col.header || col.label,
              type: col.type,
              ellipsis: col.ellipsis,
              align: col.align,
              is_selected: col.is_selected !== false,
              customField: col.customField || col.is_custom_field || false,
            }));
          }
        }

        if (Array.isArray(dataArr)) {
            this.tableData = dataArr.map((w: any) => ({
                ...w,
                ...(w.custom_fields || {}), // Flatten custom fields
                status: this.formatStatus(w.status),
                approval_status: this.formatStatus(w.approval_status),
                raw: w,
            }));
        } else {
             this.tableData = [];
        }

        this.totalCount = res.data.total || this.tableData.length;
        this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage) || 1;
      } else {
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 0;
      }
    } catch (error) {
      console.error('Error loading write-offs:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
    } finally {
      if (!skipLoading) this.isLoading = false;
    }
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id;
    this.columnConfigs = event.columns;
    this.loadWriteOffs(this.pageIndex);
  }

  formatStatus(status: string): string {
    if (!status) return '-';
    return status
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  onFilterEvent(event: FilterEvent) {
    const extractValue = (val: any) => (typeof val === 'object' && val !== null) ? val.value : val;
    this.assetIdSearch = extractValue(event.data?.assetIdSearch) || null;
    this.assetNameQuery = extractValue(event.data?.assetNameQuery) || null;

    const newCenterId = event.data?.centerId || null;
    if (newCenterId !== this.centerId) {
      this.centerId = newCenterId;
      this.locationId = null; // Reset location when center changes
      this.updateLocationOptions(this.centerId);
    } else {
      this.locationId = event.data?.locationId || null;
    }
    
    this.listStateService.clearPage();
    this.loadWriteOffs(1);
  }

  async onActionClicked(event: ActionEvent) {
    const row = event.row?.raw || event.row;
    const id = String(row.id);

    switch (event.action) {
      case 'view':
        this.router.navigate(['/assets/write-offs', id]);
        break;
      case 'edit':
        this.router.navigate(['/assets/write-offs', id, 'edit']);
        break;
      case 'delete':
        if (confirm('Are you sure you want to delete this asset write-off?')) {
          try {
            await lastValueFrom(this.writeOffService.deleteAssetWriteOff(id));
            this.toasterService.success('Asset write-off deleted successfully');
            await this.loadWriteOffs(this.pageIndex);
          } catch (error) {
            console.error('Failed to delete asset write-off:', error);
            this.toasterService.error('Failed to delete asset write-off. Please try again.');
          }
        }
        break;
    }
  }

  onPageChanged(page: number) {
    this.listStateService.setPage(page);
  }

  onColumnsSelected(cols: string[]) {
    // Handle column selection persistence if needed
  }

  exportCsv() {
    this.openExportDialog('csv');
  }

  exportXlsx() {
    this.openExportDialog('excel');
  }

  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Asset Write-Offs',
      width: '500px',
      data: {
        module: 'asset_write_off',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        defaultFormat: format,
      },
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  private getCurrentFilters(): Record<string, any> {
    return {
      asset_id: this.assetIdSearch || undefined,
      search: this.assetNameQuery || undefined,
      subsidiary_id: this.centerId || undefined,
      location_id: this.locationId || undefined,
    };
  }

  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started',
          'Your export is processing. Click here to view progress →',
          5000
        );
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      },
    });
  }
}
